import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { eventsApi, usersApi } from '../../services/api';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE, assemblePhone, splitPhone } from '../../config';
import { Event } from '../../types';

export interface EditableUser {
  userId: string;
  eventId?: string;
  name: string;
  email: string;
  phone?: string;
  occupation: string;
  businessNature: string;
}

interface EditUserModalProps {
  isOpen: boolean;
  user: EditableUser | null;
  onClose: () => void;
  /** Called after a successful save so the caller can refetch its list. */
  onSaved: () => void;
}

interface FormValues {
  name: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
  occupation: string;
  businessNature: string;
}

type FormErrors = Partial<Record<keyof FormValues, string>>;

/** Local part of the number (after the country code) may not exceed this. */
const PHONE_MAX_DIGITS = 10;

const EMPTY_FORM: FormValues = {
  name: '',
  email: '',
  phone: '',
  phoneCountryCode: DEFAULT_COUNTRY_CODE,
  occupation: '',
  businessNature: '',
};

/**
 * Edit modal for a participant listed in the User Statistics users table.
 * Only the details an admin can legitimately correct are editable — status and
 * verified state are derived server-side from chat activity / email verification
 * and are deliberately absent here.
 */
const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, user, onClose, onSaved }) => {
  const [formValues, setFormValues] = useState<FormValues>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [modalEvent, setModalEvent] = useState<Event | null>(null);
  const [saving, setSaving] = useState(false);

  // Prefill whenever a different user is opened.
  useEffect(() => {
    if (!isOpen || !user) return;

    const { countryCode, localNumber } = splitPhone(user.phone || '');
    setFormValues({
      name: user.name || '',
      email: user.email || '',
      phone: localNumber,
      phoneCountryCode: countryCode,
      occupation: user.occupation || '',
      businessNature: user.businessNature || '',
    });
    setFormErrors({});
    setModalEvent(null);

    if (!user.eventId) return;

    let cancelled = false;
    // The event's signup settings decide whether email / phone are mandatory.
    eventsApi
      .getById(user.eventId)
      .then((res) => {
        if (!cancelled && res.data?.data) setModalEvent(res.data.data);
      })
      .catch((e) => {
        // Fall back to name-only validation rather than blocking the edit.
        console.error('Failed to load event settings for edit modal:', e);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, user]);

  const emailRequired = !!(
    modalEvent?.requireEmail ||
    modalEvent?.requiresEmailVerification ||
    modalEvent?.otpChannel === 'email'
  );
  const phoneRequired = !!(modalEvent?.requirePhone || modalEvent?.otpChannel === 'whatsapp');

  const validateForm = useCallback((): boolean => {
    const errors: FormErrors = {};

    if (!formValues.name.trim()) errors.name = 'Name is required';

    if (emailRequired && !formValues.email.trim()) {
      errors.email = 'Email is required for this Knowledge Assistant';
    } else if (formValues.email.trim() && !/\S+@\S+\.\S+/.test(formValues.email)) {
      errors.email = 'Invalid email format';
    }

    if (phoneRequired && !formValues.phone.trim()) {
      errors.phone = 'Phone number is required for this Knowledge Assistant';
    } else if (formValues.phone.length > PHONE_MAX_DIGITS) {
      // Typing is capped, so this only catches an over-long number already in the DB.
      errors.phone = `Phone number cannot be more than ${PHONE_MAX_DIGITS} digits`;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formValues, emailRequired, phoneRequired]);

  const handleSave = async () => {
    if (!user || !validateForm()) return;

    setSaving(true);
    try {
      await usersApi.update(user.userId, {
        name: formValues.name.trim(),
        email: formValues.email.trim(),
        phone: formValues.phone.trim()
          ? assemblePhone(formValues.phoneCountryCode, formValues.phone)
          : '',
        occupation: formValues.occupation.trim(),
        businessNature: formValues.businessNature.trim(),
        // Scopes the update to the right event — userId is only unique per event.
        ...(user.eventId ? { eventId: user.eventId } : {}),
      });

      toast.success('User updated');
      onClose();
      onSaved();
    } catch (error: any) {
      const conflict = error?.response?.data?.data?.conflict;
      if (error?.response?.status === 409 && conflict) {
        // Show it against the offending field instead of a drive-by toast.
        if (conflict.type === 'email') {
          setFormErrors((prev) => ({
            ...prev,
            email: `Already registered in this event to ${conflict.userName || 'another user'}${
              conflict.userPhone ? ` (${conflict.userPhone})` : ''
            }.`,
          }));
        } else {
          setFormErrors((prev) => ({
            ...prev,
            phone: `Already registered in this event to ${conflict.userName || 'another user'}${
              conflict.userEmail ? ` (${conflict.userEmail})` : ''
            }.`,
          }));
        }
        return;
      }
      const data = error?.response?.data;
      toast.error(data?.message || data?.error || error?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
            <p className="text-sm text-gray-500 mt-0.5">Update details for {user.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formValues.name}
              onChange={(e) => setFormValues((v) => ({ ...v, name: e.target.value }))}
              placeholder="Enter user name"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                formErrors.name ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email {emailRequired && <span className="text-red-500">*</span>}
            </label>
            <input
              type="email"
              value={formValues.email}
              onChange={(e) => setFormValues((v) => ({ ...v, email: e.target.value }))}
              placeholder="Enter email address"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                formErrors.email ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Phone Number {phoneRequired && <span className="text-red-500">*</span>}
            </label>
            <div className="flex gap-2">
              <select
                value={formValues.phoneCountryCode}
                onChange={(e) => setFormValues((v) => ({ ...v, phoneCountryCode: e.target.value }))}
                className="rounded-lg border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} {c.name}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={PHONE_MAX_DIGITS}
                value={formValues.phone}
                onChange={(e) =>
                  setFormValues((v) => ({
                    ...v,
                    // Digits only, hard-capped — typing past the 10th does nothing.
                    phone: e.target.value.replace(/\D/g, '').slice(0, PHONE_MAX_DIGITS),
                  }))
                }
                placeholder="10-digit number"
                className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.phone ? 'border-red-400' : 'border-gray-300'
                }`}
              />
            </div>
            {formErrors.phone && <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>}
          </div>

          {/* Occupation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Occupation</label>
            <input
              type="text"
              value={formValues.occupation}
              onChange={(e) => setFormValues((v) => ({ ...v, occupation: e.target.value }))}
              placeholder="e.g. Software Engineer, Student, Business Owner"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Business Nature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Business Nature / Industry
            </label>
            <input
              type="text"
              value={formValues.businessNature}
              onChange={(e) => setFormValues((v) => ({ ...v, businessNature: e.target.value }))}
              placeholder="e.g. Technology, Healthcare, Education"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
